import type { ValueOf, WithDefault } from "@mcwd/typescript-type-guards";
import type { HtmlInputTypeName } from "../../../../types-and-definitions/forms/input-element.js";
import type { Marketo_InputMeta } from "./form-input-meta.js";
import { UserMarketoInputName, type HiddenMarketoInputName } from "./marketo-input-names-and-values.js";

type GetInputMetaTypeFromInputName<TInputName extends UserMarketoInputName | HiddenMarketoInputName> =
  TInputName extends UserMarketoInputName 
    ? Marketo_InputMeta.UserInput
    :
  TInputName extends HiddenMarketoInputName
    ? Marketo_InputMeta.HiddenInput
    : never;

type MarketoInputMetaType<TInputName extends UserMarketoInputName | HiddenMarketoInputName> = {
  [key in TInputName]: GetInputMetaTypeFromInputName<TInputName>
}


function createMarketoUserInputMeta<
  const TType extends Exclude<HtmlInputTypeName, "hidden">,
  const TMktoName extends UserMarketoInputName
> (
  { type, marketoName }: { type: TType, marketoName: TMktoName }
): { readonly type: TType, readonly marketoType: TType, readonly marketoName: TMktoName, readonly name: `eclipse_${ TMktoName }`, readonly isMarketoInput: true };
function createMarketoUserInputMeta<
  const TType extends Exclude<HtmlInputTypeName, "hidden">,
  const TMktoName extends UserMarketoInputName,
  const TMktoType extends HtmlInputTypeName
>(
  { type, marketoName, marketoType }: { type: TType, marketoName: TMktoName, marketoType: TMktoType }
): { readonly type: TType, readonly marketoType: TMktoType, readonly marketoName: TMktoName, readonly name: `eclipse_${ TMktoName }`, readonly isMarketoInput: true };
function createMarketoUserInputMeta<
  const TType extends Exclude<HtmlInputTypeName, "hidden">,
  const TMktoName extends UserMarketoInputName,
  const TMktoType extends HtmlInputTypeName | undefined = undefined,
>({ type, marketoName, marketoType }: { type: TType, marketoName: TMktoName, marketoType?: TMktoType })
{
  const mktoType = (marketoType ?? type) as WithDefault<TMktoType, TType>;
  return {
    name: `eclipse_${marketoName}` as const,
    marketoName,
    type,
    marketoType: mktoType,
    isMarketoInput: true
  } as const satisfies Marketo_InputMeta.UserInput<TMktoName, WithDefault<TMktoType, TType>>;
}

function createMarketoHiddenInputMeta<const TMktoName extends HiddenMarketoInputName> (
  { marketoName }: { marketoName: TMktoName }
): { readonly type: "hidden", readonly marketoType: "hidden", readonly marketoName: TMktoName, readonly name: `eclipse_${ TMktoName }`, readonly isMarketoInput: true }
{
  return {
    name: `eclipse_${marketoName}` as const,
    marketoName,
    type: "hidden",
    marketoType: "hidden",
    isMarketoInput: true
  } as const satisfies Marketo_InputMeta.HiddenInput<TMktoName>;
}

export const MarketoUserInputMetaObject = {
  FirstName: createMarketoUserInputMeta({
    marketoName: "FirstName",
    type: "text"
  }),
  LastName: createMarketoUserInputMeta({
    marketoName: "LastName",
    type: "text"
  }),
  Email: createMarketoUserInputMeta({
    marketoName: "Email",
    type: "email"
  }),
  Country: createMarketoUserInputMeta({
    marketoName: "Country",
    type: "select"
  }),
  State: createMarketoUserInputMeta({
    marketoName: "State",
    type: "select"
  }),
  Phone: createMarketoUserInputMeta({
    marketoName: "Phone",
    type: "tel"
  }),
  Company: createMarketoUserInputMeta({
    marketoName: "Company",
    type: "text"
  }),
  Tier__c: createMarketoUserInputMeta({
    marketoName: "Tier__c",
    type: "select"
  }),
  Industry: createMarketoUserInputMeta({
    marketoName: "Industry",
    type: "select"
  }),
  Department__c: createMarketoUserInputMeta({
    marketoName: "Department__c",
    type: "select"
  }),
  Email_Opt_In__c: createMarketoUserInputMeta({
    marketoName: "Email_Opt_In__c",
    type: "checkbox"
  }),
  contactRequested: createMarketoUserInputMeta({
    marketoName: "contactRequested",
    type: "checkbox"
  }),
  Webform_Comments_recent_value__c: createMarketoUserInputMeta({
    marketoName: "Webform_Comments_recent_value__c",
    type: "textarea"
  }),
  Latest_Solution_Interest__c: createMarketoUserInputMeta({
    marketoName: "Latest_Solution_Interest__c",
    type: "select"
  }),
  Partner_Opt_in_Processing__c: createMarketoUserInputMeta({
    marketoName: "Partner_Opt_in_Processing__c",
    type: "checkbox"
  })
} as const satisfies MarketoInputMetaType<UserMarketoInputName>;

export type MarketoUserInputMetaObject = typeof MarketoUserInputMetaObject;

export type MarketoUserInputMeta<MarketoInputName extends UserMarketoInputName = UserMarketoInputName> = MarketoUserInputMetaObject[MarketoInputName];

export const MarketoHiddenInputMeta = {
  SiteLanguageCode: createMarketoHiddenInputMeta({ marketoName: "SiteLanguageCode" }),
  FormName: createMarketoHiddenInputMeta({ marketoName: "FormName" }),
  source: createMarketoHiddenInputMeta({ marketoName: "source" }),
  Source_String__c: createMarketoHiddenInputMeta({ marketoName: "Source_String__c" }),
  Source_Website: createMarketoHiddenInputMeta({ marketoName: "Source_Website" }),
  Medium__c: createMarketoHiddenInputMeta({ marketoName: "Medium__c" }),
  Campaign__c: createMarketoHiddenInputMeta({ marketoName: "Campaign__c" }),
  Source__c: createMarketoHiddenInputMeta({ marketoName: "Source__c" }),
  Last_Offer_Campaign__c: createMarketoHiddenInputMeta({ marketoName: "Last_Offer_Campaign__c" }),
  Last_Offer_Name__c: createMarketoHiddenInputMeta({ marketoName: "Last_Offer_Name__c" }),
  documentDownloadURL: createMarketoHiddenInputMeta({ marketoName: "documentDownloadURL" }),
  Website_Language_Code__c: createMarketoHiddenInputMeta({ marketoName: "Website_Language_Code__c" }),
  GCLID__c: createMarketoHiddenInputMeta({ marketoName: "GCLID__c" }),
  Mobile__c: createMarketoHiddenInputMeta({ marketoName: "Mobile__c" }),
  roi_querystring__c_contact: createMarketoHiddenInputMeta({ marketoName: "roi_querystring__c_contact" }),
  initialFormGatingLevel: createMarketoHiddenInputMeta({ marketoName: "initialFormGatingLevel" }),
  initialFormExperienceGatingLevel: createMarketoHiddenInputMeta({ marketoName: "initialFormExperienceGatingLevel" }),
  formGatingState: createMarketoHiddenInputMeta({ marketoName: "formGatingState" }),
  experienceGatingState: createMarketoHiddenInputMeta({ marketoName: "experienceGatingState" }),
  multiDocSelectorFormFill: createMarketoHiddenInputMeta({ marketoName: "multiDocSelectorFormFill" }),
} as const satisfies MarketoInputMetaType<HiddenMarketoInputName>;

export type MarketoHiddenInputMeta = typeof MarketoHiddenInputMeta;

export const MarketoAllInputMeta = {
  ... MarketoUserInputMetaObject,
  ... MarketoHiddenInputMeta
} as const as (MarketoUserInputMetaObject & MarketoHiddenInputMeta);

export type MarketoAllInputMeta = typeof MarketoAllInputMeta;

export type MarketoAllInputMetaBy<TInputName extends keyof MarketoAllInputMeta> =
  ValueOf<{ [ K in TInputName ]: MarketoAllInputMeta[ TInputName ] }>;